/**
 * AudioService - Servicio para obtener audiolibros de LibriVox
 * LibriVox ofrece audiolibros gratuitos de dominio público
 */

// Cache para evitar llamadas repetidas
const cache = new Map();
const CACHE_TTL = 30 * 60 * 1000; // 30 minutos

// URLs base - usando proxies locales para evitar CORS
const LIBRIVOX_API = '/api/librivox';
const OPEN_LIBRARY_API = '/api/openlibrary';
const COVERS_BASE = 'https://covers.openlibrary.org/b';

/**
 * Obtiene datos del cache si no han expirado
 */
function getFromCache(key) {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
    }
    cache.delete(key);
    return null;
}

/**
 * Guarda datos en cache
 */
function setCache(key, data) {
    cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Mezcla aleatoriamente un array (Fisher-Yates shuffle)
 */
function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

/**
 * Busca audiolibros por título
 */
async function searchByTitle(title, limit = 20) {
    const cacheKey = `audio-title:${title}:${limit}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${LIBRIVOX_API}/audiobooks?title=^${encodeURIComponent(title)}&format=json`
        );
        const data = await response.json();

        let audiobooks = (data.books || [])
            .slice(0, limit)
            .map(book => normalizeAudiobook(book));

        // Enrich with Open Library covers
        audiobooks = await enrichWithCovers(audiobooks);

        setCache(cacheKey, audiobooks);
        return audiobooks;
    } catch (error) {
        console.error('Error searching audiobooks by title:', error);
        return [];
    }
}

/**
 * Busca audiolibros por autor
 */
async function searchByAuthor(author, limit = 20) {
    const cacheKey = `audio-author:${author}:${limit}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${LIBRIVOX_API}/audiobooks?author=^${encodeURIComponent(author)}&format=json`
        );
        const data = await response.json();

        let audiobooks = (data.books || [])
            .slice(0, limit)
            .map(book => normalizeAudiobook(book));

        // Enrich with Open Library covers
        audiobooks = await enrichWithCovers(audiobooks);

        setCache(cacheKey, audiobooks);
        return audiobooks;
    } catch (error) {
        console.error('Error searching audiobooks by author:', error);
        return [];
    }
}

/**
 * Busca audiolibros por género
 */
async function searchByGenre(genre, limit = 20) {
    const cacheKey = `audio-genre:${genre}:${limit}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${LIBRIVOX_API}/audiobooks?genre=^${encodeURIComponent(genre)}&format=json`
        );
        const data = await response.json();

        let audiobooks = (data.books || [])
            .slice(0, limit)
            .map(book => normalizeAudiobook(book));

        // Enrich with Open Library covers
        audiobooks = await enrichWithCovers(audiobooks);

        setCache(cacheKey, audiobooks);
        return audiobooks;
    } catch (error) {
        console.error('Error searching audiobooks by genre:', error);
        return [];
    }
}

/**
 * Búsqueda general de audiolibros
 */
async function searchAudiobooks(query, limit = 20) {
    // LibriVox no tiene búsqueda general, intentamos por título y autor
    const [byTitle, byAuthor] = await Promise.all([
        searchByTitle(query, limit),
        searchByAuthor(query, limit)
    ]);

    // Combinar resultados eliminando duplicados
    const combined = [...byTitle];
    const ids = new Set(byTitle.map(a => a.id));

    for (const audiobook of byAuthor) {
        if (!ids.has(audiobook.id)) {
            combined.push(audiobook);
            ids.add(audiobook.id);
        }
    }

    return combined.slice(0, limit);
}

/**
 * Obtiene detalles de un audiolibro por ID (con capítulos)
 */
async function getAudiobookDetails(id) {
    const cacheKey = `audiobook-details:${id}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${LIBRIVOX_API}/audiobooks?id=${id}&extended=1&format=json`
        );
        const data = await response.json();

        if (!data.books || data.books.length === 0) {
            return null;
        }

        let audiobook = normalizeAudiobook(data.books[0], true);

        // Enrich with Open Library cover
        if (!audiobook.coverUrl && audiobook.title) {
            const author = audiobook.authors?.[0] || '';
            audiobook.coverUrl = await searchOpenLibraryCover(audiobook.title, author);
        }

        setCache(cacheKey, audiobook);
        return audiobook;
    } catch (error) {
        console.error('Error getting audiobook details:', error);
        return null;
    }
}

/**
 * Obtiene audiolibros recientes
 */
async function getRecentAudiobooks(limit = 20) {
    const cacheKey = `audio-recent:${limit}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${LIBRIVOX_API}/audiobooks?since=2020-01-01&format=json`
        );
        const data = await response.json();

        let audiobooks = (data.books || [])
            .slice(0, limit)
            .map(book => normalizeAudiobook(book));

        // Enrich with Open Library covers
        audiobooks = await enrichWithCovers(audiobooks);

        setCache(cacheKey, audiobooks);
        return shuffleArray(audiobooks);
    } catch (error) {
        console.error('Error getting recent audiobooks:', error);
        return [];
    }
}

/**
 * Obtiene audiolibros por categoría predefinida
 * Los audiolibros se mezclan aleatoriamente en cada carga
 */
async function getAudiobooksByCategory(category) {
    const categoryMapping = {
        'classics': 'Fiction',
        'fiction': 'Fiction',
        'poetry': 'Poetry',
        'philosophy': 'Philosophy',
        'history': 'History',
        'science': 'Science',
        'adventure': 'Adventure Fiction',
        'romance': 'Romance',
        'mystery': 'Detective Fiction',
        'fantasy': 'Fantasy'
    };

    const genre = categoryMapping[category] || category;
    const audiobooks = await searchByGenre(genre, 20);
    return shuffleArray(audiobooks);
}

/**
 * Obtiene audiolibro destacado (para el Banner)
 */
async function getFeaturedAudiobook() {
    // IDs de audiolibros populares en LibriVox (verificados)
    const featuredIds = [
        '253',    // Pride and Prejudice
        '314',    // Adventures of Sherlock Holmes
        '7904',   // Alice in Wonderland (Drama)
        '381',    // Frankenstein
        '271',    // Dracula
        '510',    // A Tale of Two Cities
        '753',    // Moby Dick
        '448',    // The Adventures of Tom Sawyer
        '133',    // Jane Eyre
        '365',    // The Picture of Dorian Gray
    ];

    // Seleccionar uno aleatorio
    const randomId = featuredIds[Math.floor(Math.random() * featuredIds.length)];

    try {
        const audiobook = await getAudiobookDetails(randomId);
        return audiobook;
    } catch (error) {
        console.error('Error getting featured audiobook:', error);
        // Fallback a búsqueda
        const audiobooks = await searchByGenre('Fiction', 1);
        return audiobooks[0] || null;
    }
}

/**
 * Busca portada en Open Library por título del libro
 */
async function searchOpenLibraryCover(title, author = '') {
    const cacheKey = `cover-search:${title}:${author}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const searchQuery = author ? `${title} ${author}` : title;
        const response = await fetch(
            `${OPEN_LIBRARY_API}/search.json?q=${encodeURIComponent(searchQuery)}&limit=1`
        );
        const data = await response.json();

        if (data.docs && data.docs.length > 0 && data.docs[0].cover_i) {
            const coverUrl = `${COVERS_BASE}/id/${data.docs[0].cover_i}-M.jpg`;
            setCache(cacheKey, coverUrl);
            return coverUrl;
        }
        return null;
    } catch (error) {
        console.error('Error searching Open Library cover:', error);
        return null;
    }
}

/**
 * Obtiene la URL de portada de un audiolibro
 * Busca en Open Library para obtener portadas de alta calidad
 */
function getCoverUrl(audiobook) {
    if (audiobook.coverUrl) return audiobook.coverUrl;
    return null;
}

/**
 * Enriquece un array de audiobooks con portadas de Open Library
 */
async function enrichWithCovers(audiobooks) {
    const enriched = await Promise.all(
        audiobooks.map(async (audiobook) => {
            if (!audiobook.coverUrl && audiobook.title) {
                const author = audiobook.authors?.[0] || '';
                const coverUrl = await searchOpenLibraryCover(audiobook.title, author);
                return { ...audiobook, coverUrl };
            }
            return audiobook;
        })
    );
    return enriched;
}

/**
 * Parsea duración en formato HH:MM:SS a segundos
 */
function parseDuration(duration) {
    if (!duration) return 0;
    const parts = duration.split(':').map(Number);
    if (parts.length === 3) {
        return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
        return parts[0] * 60 + parts[1];
    }
    return parseInt(duration) || 0;
}

/**
 * Formatea segundos a texto legible
 */
function formatDuration(seconds) {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
}

/**
 * Normaliza datos de audiolibro de LibriVox
 */
function normalizeAudiobook(book, includeChapters = false) {
    const totalSeconds = parseDuration(book.totaltimesecs?.toString() || book.totaltime);

    const normalized = {
        id: book.id,
        title: book.title,
        description: book.description || '',
        authors: book.authors?.map(a => `${a.first_name} ${a.last_name}`.trim()) || [],
        language: book.language,
        copyrightYear: book.copyright_year,
        totalTime: book.totaltime || '',
        totalSeconds: totalSeconds,
        totalTimeFormatted: formatDuration(totalSeconds),
        numSections: parseInt(book.num_sections) || 0,
        urlLibrivox: book.url_librivox,
        urlProject: book.url_project,
        urlRss: book.url_rss,
        urlZipFile: book.url_zip_file,
        coverUrl: null,
        genres: book.genres?.map(g => g.name) || [],
        source: 'librivox'
    };

    // Intentar obtener portada
    normalized.coverUrl = getCoverUrl(normalized);

    // Incluir capítulos si están disponibles
    if (includeChapters && book.sections) {
        normalized.chapters = book.sections.map((section, index) => ({
            id: section.id,
            index: index,
            title: section.section_number
                ? `Chapter ${section.section_number}: ${section.title || ''}`
                : section.title || `Chapter ${index + 1}`,
            duration: section.playtime || '',
            durationSeconds: parseDuration(section.playtime),
            listenUrl: section.listen_url,
            reader: section.readers?.[0]
                ? `${section.readers[0].display_name}`
                : 'Unknown reader'
        }));
    }

    return normalized;
}

// ============ Exports ============

const AudioService = {
    // Búsqueda
    searchAudiobooks,
    searchByTitle,
    searchByAuthor,
    searchByGenre,

    // Detalles
    getAudiobookDetails,

    // Categorías
    getRecentAudiobooks,
    getAudiobooksByCategory,
    getFeaturedAudiobook,

    // Utilidades
    getCoverUrl,
    formatDuration,
    parseDuration,
    clearCache: () => cache.clear()
};

export default AudioService;
